import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios from 'axios';

import {
  requestCreateResource,
  requestUpdateResource,
  requestDeleteResource,
  requestViewResourcesByType
} from './resource.actions';
import { requestViewAllCollections } from '../collection/collection.actions';
import {
  getCreateResourceRequest,
  getUpdateResourceRequest,
  getDeleteResourceRequest,
  getViewResourcesByTypeRequest
} from './resource.endpoints';

import { addSystemNotice, addSpinner, removeSpinner } from '../../slices/system/system.slice';

import { SUCCESS, ERROR } from '../../utils/constants';
import { getErrorMessage } from '../../utils/api-util';
import { setResources } from '../../slices/programme/plan/plan.slice';

export function* performCreateResourceRequest({ payload }) {
  const { input, programmeExternalReference, onSuccess } = payload;

  try {
    yield put(addSpinner('Creating Resource'));

    const [endpoint, options] = getCreateResourceRequest(input);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(removeSpinner('Creating Resource'));
    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(requestViewAllCollections({ externalReference: programmeExternalReference }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Creating Resource'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForCreateResourceRequest() {
  yield takeLatest(requestCreateResource.type, performCreateResourceRequest);
}

export function* performUpdateResourceRequest({ payload }) {
  const { input, programmeExternalReference, onSuccess } = payload;

  try {
    yield put(addSpinner('Updating Resource'));

    const [endpoint, options] = getUpdateResourceRequest(input);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(removeSpinner('Updating Resource'));
    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(requestViewAllCollections({ externalReference: programmeExternalReference }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Updating Resource'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForUpdateResourceRequest() {
  yield takeLatest(requestUpdateResource.type, performUpdateResourceRequest);
}

export function* performDeleteResourceRequest({ payload }) {
  const { programmeExternalReference, collectionExternalReference, resourceExternalReference, onSuccess } = payload;

  try {
    yield put(addSpinner('Deleting Resource'));

    const [endpoint, options] = getDeleteResourceRequest(
      collectionExternalReference,
      resourceExternalReference,
      programmeExternalReference
    );

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(removeSpinner('Deleting Resource'));
    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(requestViewAllCollections({ externalReference: programmeExternalReference }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Deleting Resource'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForDeleteResourceRequest() {
  yield takeLatest(requestDeleteResource.type, performDeleteResourceRequest);
}

export function* performViewResourcesByTypeRequest({ payload }) {
  const { programmeExternalReference, type, onSuccess } = payload;

  try {
    yield put(addSpinner('Retrieving Resources'));

    const [endpoint, options] = getViewResourcesByTypeRequest(programmeExternalReference, type);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(setResources(data.resources));
    yield put(removeSpinner('Retrieving Resources'));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Retrieving Resources'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForViewResourcesByTypeRequest() {
  yield takeLatest(requestViewResourcesByType.type, performViewResourcesByTypeRequest);
}

export default function* resourceSaga() {
  yield all([
    watchForCreateResourceRequest(),
    watchForUpdateResourceRequest(),
    watchForDeleteResourceRequest(),
    watchForViewResourcesByTypeRequest()
  ]);
}
